import type { ClientMessage, ServerMessage } from '../shared/messages';
import type { PlayerPublic, Profile, ProtocolId } from '../shared/types';
import type { AudioEngine } from './audio';
import type { AuthClient } from './auth';
import { GameScreen } from './game-screen';
import { ClockSync, GameSocket, wsUrl } from './net';
import type { LoreRepository } from './lore/repository';

interface QuickMatchCallbacks {
  onStatus: (text: string) => void;
  onMatched: (code: string) => void;
  onError: (message: string) => void;
}

export interface QuickMatchHandle {
  cancel: () => void;
}

export function startQuickMatch(
  auth: AuthClient,
  callbacks: QuickMatchCallbacks,
): QuickMatchHandle {
  let socket: GameSocket | null = null;
  let cancelled = false;
  let matched = false;
  let timeout: number | null = null;

  const cleanup = () => {
    if (timeout !== null) window.clearTimeout(timeout);
    socket?.close();
    socket = null;
  };

  const handle: QuickMatchHandle = {
    cancel: () => {
      if (matched) return;
      cancelled = true;
      cleanup();
    },
  };

  void (async () => {
    const token = await auth.token();
    if (!token) {
      callbacks.onError('账号会话已过期，请重新登录。');
      return;
    }
    callbacks.onStatus('SEARCHING FOR SUBJECT // 正在寻找受试者');
    socket = new GameSocket(wsUrl('lobby=quick'), {
      onOpen: () => {
        socket?.send({ type: 'auth', token });
      },
      onMessage: (message) => {
        if (message.type === 'authOk') {
          socket?.send({ type: 'queue' });
          callbacks.onStatus('QUEUED // 等待另一名受试者');
        } else if (message.type === 'queued') {
          callbacks.onStatus(`QUEUED // 队列位置 ${message.position}`);
        } else if (message.type === 'matched') {
          matched = true;
          callbacks.onMatched(message.code);
          cleanup();
        } else if (message.type === 'error') {
          if (!cancelled) {
            callbacks.onError(mapError(message.code, message.message));
            cleanup();
          }
        }
      },
      onClose: () => {
        if (!cancelled && !matched) {
          callbacks.onError('匹配链路中断。');
        }
      },
    });
    socket.connect();
    timeout = window.setTimeout(() => {
      if (!cancelled && !matched) {
        callbacks.onError('暂时没有可配对的受试者。');
        cleanup();
      }
    }, 60_000);
  })();

  return handle;
}

export interface OnlineSessionOptions {
  container: HTMLElement;
  code: string;
  auth: AuthClient;
  profile: Profile;
  audio: AudioEngine;
  lore: LoreRepository;
  onExit: () => void;
  onProfileRefresh: () => void;
  onOpenArchive: () => void;
}

export class OnlineSession {
  private readonly opts: OnlineSessionOptions;
  private readonly screen: GameScreen;
  private readonly clock = new ClockSync();
  private socket: GameSocket | null = null;
  private selfUid: string;
  private players: PlayerPublic[] = [];
  private rivalName = 'SUBJECT B';
  private closing = false;
  private ended = false;
  private authenticated = false;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private connecting = false;
  private latencyWarned = false;

  constructor(options: OnlineSessionOptions) {
    this.opts = options;
    this.selfUid = options.profile.uid;
    this.screen = new GameScreen(options.container, {
      mode: 'online',
      selfUid: this.selfUid,
      selfName: options.profile.name,
      audio: options.audio,
      lore: options.lore,
      onAnswer: (color) => this.send({ type: 'answer', qid: this.screen.currentQidValue ?? '', color }),
      onExit: () => this.leave(),
      onRematch: () => this.voteRematch(),
      onReturnLobby: () => this.leave(),
      onOpenArchive: options.onOpenArchive,
    });
    this.screen.setPlayers([], this.selfUid);
    this.screen.showWaiting(options.code, [], this.selfUid);
    this.connect(options.code);
  }

  private connect(code: string): void {
    if (this.closing) return;
    this.connecting = true;
    this.socket = new GameSocket(wsUrl(`code=${encodeURIComponent(code)}`), {
      onOpen: () => {
        void this.sendAuth();
      },
      onMessage: (message) => this.handleMessage(message),
      onClose: () => this.handleDisconnect(),
    });
    this.socket.connect();
  }

  private async sendAuth(): Promise<void> {
    const token = await this.opts.auth.token();
    if (!token) {
      this.screen.showToast('会话已过期，请重新登录。');
      this.leave();
      return;
    }
    this.send({ type: 'auth', token });
  }

  private send(message: ClientMessage): boolean {
    return this.socket?.send(message) ?? false;
  }

  private handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'hello':
        this.clock.seed(message.serverTime);
        break;
      case 'authOk':
        this.authenticated = true;
        this.connecting = false;
        this.reconnectAttempts = 0;
        this.latencyWarned = false;
        this.screen.hideReconnecting();
        this.screen.setLatency(null);
        this.startPings();
        break;
      case 'waiting':
        this.setPlayers(message.players);
        this.screen.showWaiting(message.code, message.players, this.selfUid);
        this.screen.setOpponentConnection(
          message.players.some((player) => player.uid !== this.selfUid && player.connected),
        );
        break;
      case 'countdown':
        this.setPlayers(message.players);
        this.screen.showCountdown(this.clock.toLocal(message.startAt), message.durationMs, this.selfUid);
        break;
      case 'protocolChange':
        this.screen.setProtocol(message.protocol, message.announce);
        break;
      case 'question':
        this.screen.showQuestion({
          question: message.question,
          displayAtLocal: this.clock.toLocal(message.displayAt),
          deadlineLocal: this.clock.toLocal(message.deadline),
          pollution: message.pollution,
          stabilities: message.stabilities,
        });
        break;
      case 'locked':
        this.screen.showLocked();
        break;
      case 'opponentMiss':
        this.screen.setPollution(message.pollution);
        break;
      case 'roundResult':
        this.screen.showResult(message);
        break;
      case 'suddenDeath':
        this.screen.showSuddenDeath();
        break;
      case 'matchEnd':
        this.ended = true;
        this.screen.showMatchEnd(message, this.opts.profile, this.rivalName);
        this.opts.onProfileRefresh();
        break;
      case 'rematchState': {
        const rival = this.rival();
        this.screen.setRematchState(
          message.votes,
          message.needed,
          this.selfUid,
          Boolean(rival?.connected),
        );
        break;
      }
      case 'opponentConnection':
        this.players = this.players.map((player) =>
          player.uid === message.uid ? { ...player, connected: message.connected } : player,
        );
        this.screen.setPlayers(this.players, this.selfUid);
        this.screen.setOpponentConnection(
          this.players.some((player) => player.uid !== this.selfUid && player.connected),
        );
        this.screen.showToast(
          message.connected ? '对手重新接入 // RIVAL LINKED' : '对手链路中断 // RIVAL LOST',
        );
        break;
      case 'opponentLeft':
        this.screen.setOpponentConnection(false);
        this.screen.showToast('对手已离开 // RIVAL LEFT');
        break;
      case 'state':
        this.applyState(message);
        break;
      case 'pong': {
        const rtt = this.clock.onPong(message.t, message.serverTime);
        this.send({ type: 'sync', rttMs: rtt });
        this.screen.setLatency(rtt);
        if (rtt > 200 && !this.latencyWarned) {
          this.latencyWarned = true;
          this.screen.showToast('网络延迟较高，抢答判定可能处于劣势。');
        }
        break;
      }
      case 'error':
        this.screen.showToast(mapError(message.code, message.message));
        if (message.code === 'NOT_AUTHORIZED' || message.code === 'AUTH_FAILED') {
          this.leave();
        }
        break;
      case 'left':
        break;
      default:
        break;
    }
  }

  private applyState(message: Extract<ServerMessage, { type: 'state' }>): void {
    this.screen.hideReconnecting();
    this.setPlayers(message.players);
    this.screen.setPollution(message.pollution);
    this.screen.setProtocol(message.protocol, false);
    this.screen.setStabilities(message.stabilities);
    if (message.phase === 'waiting') {
      this.screen.showWaiting(message.code, message.players, this.selfUid);
      return;
    }
    if (message.phase === 'countdown' && message.startAt !== null) {
      this.screen.showCountdown(this.clock.toLocal(message.startAt), 3000, this.selfUid);
      return;
    }
    if (message.phase === 'playing') {
      if (message.question) {
        this.screen.showQuestion({
          question: message.question.question,
          displayAtLocal: this.clock.toLocal(message.question.displayAt),
          deadlineLocal: this.clock.toLocal(message.question.deadline),
          pollution: message.pollution,
          stabilities: message.stabilities,
        });
      }
      if (message.suddenDeath) this.screen.showSuddenDeath();
      return;
    }
    if (message.phase === 'ended') {
      this.ended = true;
    }
  }

  private setPlayers(players: PlayerPublic[]): void {
    this.players = players;
    this.rivalName =
      players.find((player) => player.uid !== this.selfUid)?.name ?? 'SUBJECT B';
    this.screen.setPlayers(players, this.selfUid);
    this.screen.setOpponentConnection(
      players.some((player) => player.uid !== this.selfUid && player.connected),
    );
  }

  private rival(): PlayerPublic | undefined {
    return this.players.find((player) => player.uid !== this.selfUid);
  }

  private startPings(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    const ping = () => {
      if (this.socket?.isOpen) this.send({ type: 'ping', t: Date.now() });
    };
    ping();
    this.pingTimer = window.setInterval(ping, 2000);
  }

  private stopPings(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private voteRematch(): void {
    if (!this.ended) return;
    if (!this.send({ type: 'rematch' })) {
      this.screen.showToast('链路已断开，无法重赛。');
    }
  }

  private handleDisconnect(): void {
    if (this.closing || this.ended) return;
    if (this.connecting && this.reconnectAttempts === 0) {
      this.screen.showToast('无法接入房间。');
      this.leave();
      return;
    }
    if (this.reconnectAttempts >= 3) {
      this.screen.showToast('链路无法恢复，已退出对局。');
      this.leave();
      return;
    }
    this.reconnectAttempts += 1;
    this.stopPings();
    this.authenticated = false;
    this.screen.setLatency(null);
    this.screen.showReconnecting(this.reconnectAttempts);
    const delay = this.reconnectAttempts * 1000;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.opts.code);
    }, delay);
  }

  leave(): void {
    this.closing = true;
    this.stopPings();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.send({ type: 'leave' });
    } catch {
      // ignore
    }
    this.socket?.close();
    this.socket = null;
    this.screen.destroy();
    this.opts.onExit();
  }

  destroy(): void {
    this.closing = true;
    this.stopPings();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
    this.screen.destroy();
  }
}

export function mapError(code: string, message: string): string {
  switch (code) {
    case 'AUTH_FAILED':
      return '登录会话无效，请重新登录。';
    case 'NOT_AUTHORIZED':
      return '该账号不在这个房间。';
    case 'ROOM_FULL':
      return '房间已满。';
    case 'ROOM_STARTED':
      return '对局已经开始。';
    case 'ROOM_NOT_FOUND':
      return '房间码不存在或已失效。';
    case 'INVALID_CODE':
      return '房间码格式不正确。';
    case 'ROOM_CREATE_FAILED':
      return '房间创建失败，请重试。';
    case 'MATCH_FAILED':
      return '匹配分配失败，请重试。';
    case 'ALREADY_ANSWERED':
      return '本道题已经提交过。';
    default:
      return message || '请求失败，请重试。';
  }
}
